import React from 'react';
import { Link } from 'react-router-dom';
import { Check, Play, BookOpen, GraduationCap, Monitor, HandMetal, Award, ArrowRight } from 'lucide-react';
import { AnimatedSection } from '../components/AnimatedSection';
import { CertificationAuthority } from '../components/CertificationAuthority';
export function ProgramsPage() {
  return <div className="w-full bg-warm-white" data-id="element-538">
      {/* A) HERO */}
      <div className="bg-primary-900 text-warm-white py-20 lg:py-28" data-id="element-539">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-540">
          <h1 className="text-4xl lg:text-6xl font-heading mb-6" data-id="element-541">
            Learn Pet Emergency Skills — Your Way
          </h1>
          <p className="text-xl text-primary-100 font-body max-w-2xl mx-auto" data-id="element-542">
            From free video tips to full certification. Choose the path that
            fits your life.
          </p>
        </div>
      </div>

      {/* B) LEARNING JOURNEY */}
      <AnimatedSection className="py-24 bg-warm-white" data-id="element-543">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-544">
          <div className="text-center max-w-3xl mx-auto mb-20" data-id="element-545">
            <h2 className="text-3xl lg:text-4xl font-heading text-primary-900 mb-4" data-id="element-546">
              Your Learning Journey
            </h2>
            <p className="text-warm-gray-600 font-body text-lg" data-id="element-547">
              Start anywhere. Go as deep as you want.
            </p>
          </div>

          <div className="relative" data-id="element-548">
            {/* Connecting Line (Desktop) */}
            <div className="hidden md:block absolute top-10 left-[12.5%] right-[12.5%] h-0.5 bg-accent-200 z-0" data-id="element-549"></div>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-12 md:gap-8 relative z-10" data-id="element-550">
              {[{
              icon: BookOpen,
              step: '01',
              title: 'Discover',
              desc: 'Free videos, tips, and guides'
            }, {
              icon: Monitor,
              step: '02',
              title: 'Learn',
              desc: 'Structured video courses with quizzes'
            }, {
              icon: HandMetal,
              step: '03',
              title: 'Practice',
              desc: 'Hands-on workshops in the Bay Area'
            }, {
              icon: Award,
              step: '04',
              title: 'Certify',
              desc: 'Complete certification pathway'
            }].map((stage, i) => <div key={i} className="flex flex-col items-center text-center" data-id="element-551">
                  <div className="w-20 h-20 rounded-full bg-primary-50 flex items-center justify-center mb-6 border-4 border-warm-white shadow-warm relative" data-id="element-552">
                    <stage.icon className="w-8 h-8 text-primary-800" data-id="element-553" />
                    <div className="absolute -top-2 -right-2 w-8 h-8 rounded-full bg-accent-500 text-primary-900 font-bold font-body text-sm flex items-center justify-center border-2 border-warm-white" data-id="element-554">
                      {stage.step}
                    </div>
                  </div>
                  <h3 className="text-xl font-heading text-primary-900 mb-2" data-id="element-555">
                    {stage.title}
                  </h3>
                  <p className="text-warm-gray-600 font-body" data-id="element-556">{stage.desc}</p>
                </div>)}
            </div>
          </div>
        </div>
      </AnimatedSection>

      {/* C) ALL PROGRAMS */}
      <AnimatedSection className="py-24 bg-warm-gray-50" data-id="element-557">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-558">
          <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-559">
            <h2 className="text-3xl lg:text-4xl font-heading text-primary-900 mb-4" data-id="element-560">
              All Programs
            </h2>
            <p className="text-warm-gray-600 font-body text-lg" data-id="element-561">
              Compare options and find your fit.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8 items-stretch" data-id="element-562">
            {/* Tier 1: Free */}
            <div className="bg-white rounded-2xl p-8 shadow-warm border border-warm-gray-100 flex flex-col" data-id="element-563">
              <div className="mb-6" data-id="element-564">
                <span className="inline-block px-3 py-1 rounded-full bg-warm-gray-100 text-warm-gray-700 font-body text-xs font-bold uppercase tracking-wider mb-4" data-id="element-565">
                  Free
                </span>
                <h3 className="text-2xl font-heading text-primary-900 mb-2" data-id="element-566">
                  Pet Safety Basics
                </h3>
                <p className="text-warm-gray-600 font-body text-sm min-h-[80px]" data-id="element-567">
                  Get started with bite-sized video lessons and downloadable
                  guides. No account required.
                </p>
              </div>
              <div className="text-3xl font-heading text-primary-900 mb-6" data-id="element-568">
                Free{' '}
                <span className="text-base font-body text-warm-gray-500 font-normal" data-id="element-569">
                  forever
                </span>
              </div>
              <ul className="space-y-4 font-body text-sm text-warm-gray-700 mb-8 flex-grow" data-id="element-570">
                {['5-minute video lessons', 'Emergency quick-reference guides', 'Pet poison awareness guide', 'Seasonal hazard alerts', 'Community access'].map((feature, i) => <li key={i} className="flex items-start gap-3" data-id="element-571">
                    <Check className="w-5 h-5 text-accent-500 shrink-0 mt-0.5" data-id="element-572" />
                    <span data-id="element-573">{feature}</span>
                  </li>)}
              </ul>
              <Link to="/resources" className="w-full py-3 px-4 flex items-center justify-center gap-2 text-primary-800 font-bold font-body hover:text-accent-600 transition-colors mt-auto" data-id="element-574">
                Start Free <ArrowRight className="w-4 h-4" data-id="element-575" />
              </Link>
            </div>

            {/* Tier 2: Paid Video Course */}
            <div className="bg-white rounded-2xl p-8 shadow-warm-lg border-2 border-accent-300 flex flex-col relative transform lg:-translate-y-4" data-id="element-576">
              <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-accent-500 text-primary-900 px-4 py-1 rounded-full text-xs font-bold font-body uppercase tracking-wider" data-id="element-577">
                Most Popular
              </div>
              <div className="mb-6 mt-2" data-id="element-578">
                <h3 className="text-2xl font-heading text-primary-900 mb-2" data-id="element-579">
                  Complete Video Course
                </h3>
                <p className="text-warm-gray-600 font-body text-sm min-h-[80px]" data-id="element-580">
                  Master every core pet emergency skill through our structured,
                  self-paced video curriculum. Watch on any device, revisit
                  anytime.
                </p>
              </div>
              <div className="text-3xl font-heading text-primary-900 mb-6" data-id="element-581">
                $89{' '}
                <span className="text-base font-body text-warm-gray-500 font-normal" data-id="element-582">
                  lifetime access
                </span>
              </div>
              <ul className="space-y-4 font-body text-sm text-warm-gray-700 mb-8 flex-grow" data-id="element-583">
                {['Full CPR & First Aid curriculum', '25+ HD video demonstrations', 'Knowledge checks after each module', 'Certificate of completion', 'Lifetime access & updates', 'Priority email support'].map((feature, i) => <li key={i} className="flex items-start gap-3" data-id="element-584">
                    <Check className="w-5 h-5 text-accent-500 shrink-0 mt-0.5" data-id="element-585" />
                    <span data-id="element-586">{feature}</span>
                  </li>)}
              </ul>
              <Link to="/video-course" className="w-full py-3 px-4 text-center border-2 border-primary-800 text-primary-800 rounded-xl font-bold font-body hover:bg-primary-50 transition-colors mt-auto" data-id="element-587">
                Enroll Now
              </Link>
            </div>

            {/* Tier 3: In-Person Workshop */}
            <div className="bg-white rounded-2xl p-8 shadow-warm border border-warm-gray-100 flex flex-col" data-id="element-588">
              <div className="mb-6" data-id="element-589">
                <span className="inline-block px-3 py-1 rounded-full bg-primary-100 text-primary-800 font-body text-xs font-bold uppercase tracking-wider mb-4" data-id="element-590">
                  Bay Area
                </span>
                <h3 className="text-2xl font-heading text-primary-900 mb-2" data-id="element-591">
                  Hands-On Workshop
                </h3>
                <p className="text-warm-gray-600 font-body text-sm min-h-[80px]" data-id="element-592">
                  Nothing replaces hands-on practice. We bring professional
                  mannequins and equipment to your location for immersive,
                  instructor-led training.
                </p>
              </div>
              <div className="text-3xl font-heading text-primary-900 mb-6" data-id="element-593">
                $199{' '}
                <span className="text-base font-body text-warm-gray-500 font-normal" data-id="element-594">
                  / group
                </span>
              </div>
              <ul className="space-y-4 font-body text-sm text-warm-gray-700 mb-8 flex-grow" data-id="element-595">
                {['Everything in Video Course', 'Hands-on CPR practice with mannequins', 'Real-time instructor feedback', 'Custom scenario training', 'We come to your location', 'Group discounts available'].map((feature, i) => <li key={i} className="flex items-start gap-3" data-id="element-596">
                    <Check className="w-5 h-5 text-accent-500 shrink-0 mt-0.5" data-id="element-597" />
                    <span data-id="element-598">{feature}</span>
                  </li>)}
              </ul>
              <Link to="/workshops" className="w-full py-3 px-4 text-center bg-primary-800 text-warm-white rounded-xl font-bold font-body hover:bg-primary-700 transition-colors mt-auto" data-id="element-599">
                Request Workshop
              </Link>
            </div>

            {/* Tier 4: Certification */}
            <div className="bg-white rounded-2xl p-8 shadow-warm border border-warm-gray-100 flex flex-col" data-id="element-600">
              <div className="mb-6" data-id="element-601">
                <span className="inline-block px-3 py-1 rounded-full bg-warm-gray-800 text-warm-white font-body text-xs font-bold uppercase tracking-wider mb-4" data-id="element-602">
                  Professional
                </span>
                <h3 className="text-2xl font-heading text-primary-900 mb-2" data-id="element-603">
                  Full Certification
                </h3>
                <p className="text-warm-gray-600 font-body text-sm min-h-[80px]" data-id="element-604">
                  The complete pathway. Combine online learning with in-person
                  skills assessment to earn your official Pet CPR & First Aid
                  Certification.
                </p>
              </div>
              <div className="text-3xl font-heading text-primary-900 mb-6" data-id="element-605">
                $299{' '}
                <span className="text-base font-body text-warm-gray-500 font-normal" data-id="element-606">
                  starting at
                </span>
              </div>
              <ul className="space-y-4 font-body text-sm text-warm-gray-700 mb-8 flex-grow" data-id="element-607">
                {['Complete Video Course included', 'In-person skills assessment', '2-year professional certification', 'Digital badge & printed certificate', 'Continuing education credits', 'Employer verification portal'].map((feature, i) => <li key={i} className="flex items-start gap-3" data-id="element-608">
                    <Check className="w-5 h-5 text-accent-500 shrink-0 mt-0.5" data-id="element-609" />
                    <span data-id="element-610">{feature}</span>
                  </li>)}
              </ul>
              <Link to="/contact" className="w-full py-3 px-4 text-center bg-accent-500 text-primary-900 rounded-xl font-bold font-body hover:bg-accent-400 transition-colors mt-auto" data-id="element-611">
                Get Certified
              </Link>
            </div>
          </div>
        </div>
      </AnimatedSection>

      {/* D) COMPARISON TABLE */}
      <AnimatedSection className="py-24 bg-warm-white" data-id="element-612">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-613">
          <div className="text-center mb-16" data-id="element-614">
            <h2 className="text-3xl lg:text-4xl font-heading text-primary-900 mb-4" data-id="element-615">
              Compare Programs
            </h2>
          </div>

          <div className="overflow-x-auto" data-id="element-616">
            <table className="w-full text-left border-collapse min-w-[800px]" data-id="element-617">
              <thead data-id="element-618">
                <tr className="border-b-2 border-warm-gray-200" data-id="element-619">
                  <th className="py-4 px-6 font-heading text-xl text-primary-900 w-1/3" data-id="element-620">
                    Features
                  </th>
                  <th className="py-4 px-6 font-heading text-lg text-primary-900 text-center" data-id="element-621">
                    Free
                  </th>
                  <th className="py-4 px-6 font-heading text-lg text-primary-900 text-center" data-id="element-622">
                    Video Course
                  </th>
                  <th className="py-4 px-6 font-heading text-lg text-primary-900 text-center" data-id="element-623">
                    Workshop
                  </th>
                  <th className="py-4 px-6 font-heading text-lg text-primary-900 text-center" data-id="element-624">
                    Certification
                  </th>
                </tr>
              </thead>
              <tbody className="font-body text-warm-gray-700" data-id="element-625">
                {[{
                name: 'Video lessons',
                free: true,
                video: true,
                workshop: true,
                cert: true
              }, {
                name: 'Emergency guides',
                free: true,
                video: true,
                workshop: true,
                cert: true
              }, {
                name: 'Full curriculum',
                free: false,
                video: true,
                workshop: true,
                cert: true
              }, {
                name: 'Knowledge checks',
                free: false,
                video: true,
                workshop: true,
                cert: true
              }, {
                name: 'Certificate of completion',
                free: false,
                video: true,
                workshop: true,
                cert: true
              }, {
                name: 'Hands-on practice',
                free: false,
                video: false,
                workshop: true,
                cert: true
              }, {
                name: 'Instructor feedback',
                free: false,
                video: false,
                workshop: true,
                cert: true
              }, {
                name: '2-year certification',
                free: false,
                video: false,
                workshop: false,
                cert: true
              }, {
                name: 'CE credits',
                free: false,
                video: false,
                workshop: false,
                cert: true
              }].map((row, i) => <tr key={i} className="border-b border-warm-gray-100 hover:bg-warm-gray-50 transition-colors" data-id="element-626">
                    <td className="py-4 px-6 font-medium" data-id="element-627">{row.name}</td>
                    <td className="py-4 px-6 text-center" data-id="element-628">
                      {row.free ? <Check className="w-5 h-5 text-accent-500 mx-auto" data-id="element-629" /> : <span className="text-warm-gray-300" data-id="element-630">-</span>}
                    </td>
                    <td className="py-4 px-6 text-center" data-id="element-631">
                      {row.video ? <Check className="w-5 h-5 text-accent-500 mx-auto" data-id="element-632" /> : <span className="text-warm-gray-300" data-id="element-633">-</span>}
                    </td>
                    <td className="py-4 px-6 text-center" data-id="element-634">
                      {row.workshop ? <Check className="w-5 h-5 text-accent-500 mx-auto" data-id="element-635" /> : <span className="text-warm-gray-300" data-id="element-636">-</span>}
                    </td>
                    <td className="py-4 px-6 text-center" data-id="element-637">
                      {row.cert ? <Check className="w-5 h-5 text-accent-500 mx-auto" data-id="element-638" /> : <span className="text-warm-gray-300" data-id="element-639">-</span>}
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>
        </div>
      </AnimatedSection>

      {/* D.5) CERTIFICATION AUTHORITY */}
      <CertificationAuthority data-id="element-640" />

      {/* E) CTA SECTION */}
      <AnimatedSection className="py-24 bg-primary-800 text-center" data-id="element-641">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-642">
          <h2 className="text-3xl lg:text-4xl font-heading text-warm-white mb-6" data-id="element-643">
            Not Sure Where to Start?
          </h2>
          <p className="text-lg text-primary-100 font-body mb-10" data-id="element-644">
            Try our free lessons first. No commitment, no credit card.
          </p>
          <div className="flex flex-col sm:flex-row justify-center items-center gap-4" data-id="element-645">
            <Link to="/resources" className="inline-flex justify-center items-center px-8 py-4 border-2 border-warm-white text-lg font-bold rounded-full text-warm-white bg-transparent hover:bg-white/10 transition-all" data-id="element-646">
              Start Free
            </Link>
            <Link to="/contact" className="inline-flex justify-center items-center px-8 py-4 border border-transparent text-lg font-bold rounded-full text-primary-900 bg-accent-500 hover:bg-accent-400 shadow-warm-lg transition-all hover:-translate-y-1" data-id="element-647">
              Talk to Us
            </Link>
          </div>
        </div>
      </AnimatedSection>
    </div>;
}